/** Os eixos da frota por CSV (Samuel, 14/10/2026): "vou fazer um Excel de eixo por
 *  placa, e você sobe CSV". O arquivo vai primeiro em PRÉVIA, que diz o que muda, o
 *  que não casou e o que está ambíguo; só o Gravar escreve. A placa casa pela régua de
 *  sempre, inclusive a antiga do veículo convertido ao Mercosul. */
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Typography from '@mui/material/Typography'
import { FileUp } from 'lucide-react'
import { enviarEixos, type RelatorioEixos } from './eixosApi'
import { apiErrorMessage } from '../../shared/apiError'
import { useToast } from '../../shared/feedback/toast'
import { useI18n } from '@/core/i18n/useI18n'

function Contagem({ rotulo, valor }: { rotulo: string; valor: number }) {
  return (
    <Box sx={{ display: 'grid' }}>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{rotulo}</Typography>
      <Typography variant="h6" sx={{ fontVariantNumeric: 'tabular-nums' }}>{valor}</Typography>
    </Box>
  )
}

export function ImportarEixosDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useI18n()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [arquivo, setArquivo] = useState<File | null>(null)
  const [previa, setPrevia] = useState<RelatorioEixos | null>(null)
  const [enviando, setEnviando] = useState(false)

  function fechar() {
    setArquivo(null)
    setPrevia(null)
    onClose()
  }

  async function conferir(escolhido: File) {
    setArquivo(escolhido)
    setPrevia(null)
    setEnviando(true)
    try {
      setPrevia(await enviarEixos(escolhido, false))
    } catch (error) {
      toast.error(apiErrorMessage(error))
    } finally {
      setEnviando(false)
    }
  }

  async function gravar() {
    if (!arquivo) return
    setEnviando(true)
    try {
      const rel = await enviarEixos(arquivo, true)
      toast.success(t('cadastros.eixos.gravado', { n: rel.alteradas }))
      await queryClient.invalidateQueries({ queryKey: ['fleet'] })
      fechar()
    } catch (error) {
      toast.error(apiErrorMessage(error))
    } finally {
      setEnviando(false)
    }
  }

  const problemas = previa ? previa.sem_cadastro.length + previa.ambiguas.length + previa.invalidas.length + previa.repetidas.length : 0

  return (
    <Dialog open={open} onClose={fechar} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontFamily: '"Fredoka", sans-serif', display: 'flex', alignItems: 'center', gap: 1 }}>
        <FileUp size={18} />
        {t('cadastros.eixos.titulo')}
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>{t('cadastros.eixos.instrucao')}</Typography>
        <Box>
          <Button component="label" variant="outlined" startIcon={<FileUp size={16} />} disabled={enviando}>
            {arquivo ? arquivo.name : t('cadastros.eixos.escolher')}
            <input hidden type="file" accept=".csv,text/csv" aria-label={t('cadastros.eixos.escolher')}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) void conferir(f); e.target.value = '' }} />
          </Button>
        </Box>
        {previa && (
          <>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Contagem rotulo={t('cadastros.eixos.linhas')} valor={previa.linhas} />
              <Contagem rotulo={t('cadastros.eixos.alteradas')} valor={previa.alteradas} />
              <Contagem rotulo={t('cadastros.eixos.iguais')} valor={previa.iguais} />
              <Contagem rotulo={t('cadastros.eixos.problemas')} valor={problemas} />
            </Box>
            {previa.sem_cadastro.length > 0 && (
              <Alert severity="warning">
                <Box component="span" sx={{ fontWeight: 600, display: 'block' }}>{t('cadastros.eixos.semCadastro')}</Box>
                {previa.sem_cadastro.map((s) => s.placa).join(', ')}
              </Alert>
            )}
            {previa.ambiguas.length > 0 && (
              <Alert severity="warning">
                <Box component="span" sx={{ fontWeight: 600, display: 'block' }}>{t('cadastros.eixos.ambiguas')}</Box>
                {previa.ambiguas.map((s) => s.placa).join(', ')}
              </Alert>
            )}
            {previa.repetidas.length > 0 && (
              <Alert severity="error">
                <Box component="span" sx={{ fontWeight: 600, display: 'block' }}>{t('cadastros.eixos.repetidas')}</Box>
                {previa.repetidas.map((s) => s.placa).join(', ')}
              </Alert>
            )}
            {previa.invalidas.length > 0 && (
              <Alert severity="error">
                <Box component="span" sx={{ fontWeight: 600, display: 'block' }}>{t('cadastros.eixos.invalidas')}</Box>
                {previa.invalidas.map((s) => t('cadastros.eixos.linhaN', { n: s.linha, placa: s.placa || '' })).join(', ')}
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button color="inherit" onClick={fechar}>{t('common.close')}</Button>
        <Button variant="contained" disabled={!previa || previa.alteradas === 0 || enviando} onClick={() => void gravar()}>
          {t('cadastros.eixos.gravar', { n: previa?.alteradas ?? 0 })}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
